import { useState } from "react";
import { Select } from "components/Select";
import "./index.css";

import {
  ConditionGroup,
  OrganizationDataKeyResponse,
  QueryCrmService,
  ComparisonOperator,
  ExportsService,
  ApiError,
} from "client/openapi";
import { ConditionGroupBuilder, OptionalNote, emptyGroup } from "./builder";
import { FormOption, SubjectOption } from "./editor";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Tag } from "components/Tag/TagChip";
import { TagColor, TagSize } from "components/Tag";
import { saveAs } from "file-saver";
import Notifications from "util/notifications";
import {
  isSingleEntityQueryValid,
  isMultiEntityQueryValid,
  withoutBlankSide,
} from "./validation";
import {
  PERSON_TITLES,
  PersonKind,
  ROLE_OF,
  SEARCH_PEOPLE,
  SEARCH_TYPE_OPTIONS,
  SearchType,
} from "./searchTypes";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import { DialogContent, DialogTrigger } from "components/Dialog";
import { Dialog } from "components/Dialog";

/**
 * Props:
 * - orgId: The current organization ID.
 * - studentKeys: array of OrgDataKeys for students.
 * - parentKeys: array of OrgDataKeys for parents.
 */
export function CRMsearch({
  orgId,
  studentKeys,
  parentKeys,
  tutorKeys,
  subjects,
  forms,
}: {
  orgId: number;
  studentKeys: Array<OrganizationDataKeyResponse>;
  parentKeys: Array<OrganizationDataKeyResponse>;
  tutorKeys: Array<OrganizationDataKeyResponse>;
  subjects: SubjectOption[];
  forms: FormOption[];
}) {
  // Let user pick an endpoint to call
  const [endpoint, setEndpoint] = useState<SearchType>("get_students");
  const [isExporting, setIsExporting] = useState(false);

  // Single or dual ConditionGroup
  const [condGroupA, setCondGroupA] = useState<ConditionGroup>(emptyGroup());
  const [condGroupB, setCondGroupB] = useState<ConditionGroup>(emptyGroup());

  const [peopleA, peopleB] = SEARCH_PEOPLE[endpoint];
  const keysFor = (kind: PersonKind | null) =>
    kind === "parents"
      ? parentKeys
      : kind === "tutors"
      ? tutorKeys
      : studentKeys;
  // Whose built-in fields each group offers.
  const roleFor = (kind: PersonKind | null) => ROLE_OF[kind ?? "students"];

  // For demonstration only
  const [queryResults, setQueryResults] = useState<any>(null);

  /** Checks if the current query is valid */
  function isQueryValid(): boolean {
    if (isMultiEntity) {
      return isMultiEntityQueryValid(
        condGroupA,
        condGroupB,
        condGroupAKeys,
        condGroupBKeys
      );
    }
    return isSingleEntityQueryValid(condGroupA, condGroupAKeys);
  }

  /** Handler for exporting the data */
  async function handleExport() {
    if (!orgId || !queryResults || queryResults.length === 0) {
      return Notifications.error("No results to export");
    }

    setIsExporting(true);
    try {
      const ids = queryResults.map((result: any) => result.id);
      let data;

      if (peopleA === "students") {
        data = await ExportsService.exportStudents({
          orgId,
          requestBody: ids,
        });
      } else if (peopleA === "tutors") {
        data = await ExportsService.exportTutors({
          orgId,
          requestBody: ids,
        });
      } else {
        data = await ExportsService.exportParents({
          orgId,
          requestBody: ids,
        });
      }

      const blob = new Blob([data], { type: "text/csv" });
      const entityType = peopleA;
      const fileName = `${entityType}_query_results.csv`;

      saveAs(blob, fileName);
      Notifications.success(
        `${
          entityType.charAt(0).toUpperCase() + entityType.slice(1)
        } data has been exported successfully!`
      );
    } catch (err) {
      const error = err as ApiError;
      console.error("Export error:", {
        status: error.status,
        message: error.message,
        error,
      });
      Notifications.error(`Failed to export data: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  }

  /** Handler for the "Run Query" button */
  async function handleRunQuery() {
    // Helper to process condition groups before sending
    const processConditionGroup = (group: ConditionGroup): ConditionGroup => {
      return {
        ...group,
        conditions: group.conditions.map((condition) => {
          if ("conditions" in condition) {
            // This is a nested group
            return processConditionGroup(condition as ConditionGroup);
          } else {
            // This is a single condition
            const isListOp = [
              ComparisonOperator.IN,
              ComparisonOperator.NOT_IN,
            ].includes(condition.comparison_operator);

            if (isListOp) {
              // Get the data type for this condition
              const key = condition.org_data_key_id;
              const keyDef = [...studentKeys, ...parentKeys, ...tutorKeys].find(
                (k) => k.id === key
              );

              if (typeof condition.value === "string") {
                // Handle text input cases
                const values = condition.value.split(",");

                if (keyDef?.data_type === "FLOAT") {
                  // Convert to numbers, filter out invalid ones
                  const numberValues = values
                    .map((v) => v.trim())
                    .filter((v) => v !== "")
                    .map((v) => parseFloat(v))
                    .filter((n) => !isNaN(n));
                  return { ...condition, value: numberValues };
                } else {
                  // Keep as strings, just trim and filter empty
                  const textValues = values
                    .map((v) => v.trim())
                    .filter((v) => v !== "");
                  return { ...condition, value: textValues };
                }
              } else if (Array.isArray(condition.value)) {
                // Handle array values (from selects)
                if (keyDef?.data_type === "CUSTOM_DATA_TYPE") {
                  // Convert custom field values to strings
                  return {
                    ...condition,
                    value: condition.value.map((v) => String(v)),
                  };
                }
              }
            }
            return condition;
          }
        }),
      };
    };

    // A combined search's blank side goes as no conditions: no filter.
    const A = processConditionGroup(withoutBlankSide(condGroupA));
    const B = processConditionGroup(withoutBlankSide(condGroupB));
    // Typed by search type, so the compiler catches a missing one.
    const search: Record<SearchType, () => Promise<unknown[]>> = {
      get_students: () =>
        QueryCrmService.getStudents({ orgId, requestBody: A }),
      get_parents: () => QueryCrmService.getParents({ orgId, requestBody: A }),
      get_tutors: () => QueryCrmService.getTutors({ orgId, requestBody: A }),
      get_students_with_parents: () =>
        QueryCrmService.getStudentsWithParents({
          orgId,
          requestBody: { student_criteria: A, parent_criteria: B },
        }),
      get_parents_with_students: () =>
        QueryCrmService.getParentsWithStudents({
          orgId,
          requestBody: { parent_criteria: A, student_criteria: B },
        }),
      get_students_with_tutors: () =>
        QueryCrmService.getStudentsWithTutors({
          orgId,
          requestBody: { student_criteria: A, tutor_criteria: B },
        }),
      get_tutors_with_students: () =>
        QueryCrmService.getTutorsWithStudents({
          orgId,
          requestBody: { tutor_criteria: A, student_criteria: B },
        }),
    };
    search[endpoint]()
      .then((results) => {
        Notifications.success("Search complete!");
        setQueryResults(results);
      })
      .catch(() => {
        Notifications.error("Invalid search criteria");
        setQueryResults(null);
      });
  }

  // Decide how many builders to show
  const isMultiEntity = peopleB !== null;

  const condGroupAKeys = keysFor(peopleA);
  const condGroupBKeys = keysFor(peopleB);

  return (
    <div className="query--container">
      <div style={{ marginBottom: "1rem" }}>
        <Select
          className="max-w-[750px]"
          value={
            SEARCH_TYPE_OPTIONS.find((opt) => opt.value === endpoint) || null
          }
          onChange={(opt) => {
            if (opt && opt.value !== endpoint) {
              const next = opt.value as SearchType;
              const [nextA, nextB] = SEARCH_PEOPLE[next];
              // Fields belong to one kind of person, so start a group over
              // only when its kind changes.
              setEndpoint(next);
              if (nextA !== peopleA) setCondGroupA(emptyGroup());
              if (nextB !== peopleB) setCondGroupB(emptyGroup());
              setQueryResults(null);
            }
          }}
          options={SEARCH_TYPE_OPTIONS}
          isClearable={false}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>

      {/* Condition Group A always shown. Title it accordingly. */}
      <h3>
        <strong>{PERSON_TITLES[peopleA]}</strong>
        {isMultiEntity && <OptionalNote />}
      </h3>

      <ConditionGroupBuilder
        value={condGroupA}
        onChange={(updated) => setCondGroupA(updated)}
        availableKeys={condGroupAKeys}
        personType={roleFor(peopleA)}
        subjects={subjects}
        forms={forms}
      />

      {isMultiEntity && (
        <>
          <hr style={{ margin: "1rem 0" }} />
          <h3>
            <strong>{peopleB ? PERSON_TITLES[peopleB] : ""}</strong>
            <OptionalNote />
          </h3>
          <ConditionGroupBuilder
            value={condGroupB}
            onChange={(updated) => setCondGroupB(updated)}
            availableKeys={condGroupBKeys}
            personType={roleFor(peopleB)}
            subjects={subjects}
            forms={forms}
          />
        </>
      )}

      <div style={{ marginTop: "1rem" }}>
        <Button
          onClick={handleRunQuery}
          color={!isQueryValid() ? ButtonColor.GRAY : ButtonColor.GREEN}
          disabled={!isQueryValid()}
        >
          Run Query
        </Button>
      </div>

      <pre style={{ marginTop: "1rem", background: "#f5f5f5" }}>
        <div className="mb-4 font-bold">Results:</div>
        <div className="results-container">
          {queryResults?.map((result: any) => (
            <span className="results-tag" key={result.id}>
              <Tag
                color={TagColor.GREEN}
                size={TagSize.NARROW}
                item={`${result.first_name} ${result.last_name}`}
              />
            </span>
          ))}
        </div>
        {queryResults && queryResults.length > 0 && (
          <div className="flex flex-row gap-4">
            <div className="mt-4 mb-4">
              <Button
                onClick={handleExport}
                color={ButtonColor.PURPLE}
                size={ButtonSize.SMALL}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </div>
            <div className="mt-4 mb-4">
              <Dialog>
                <DialogTrigger asChild>
                  <Button color={ButtonColor.PURPLE} size={ButtonSize.SMALL}>
                    Compose Message
                  </Button>
                </DialogTrigger>

                {/* Keyed on the results, so a re-run search never sends to
                    the people of an earlier run. */}
                <ComposeMessageDialog
                  key={`${endpoint}:${queryResults
                    .map((r: any) => r.id)
                    .join(",")}`}
                  students={peopleA === "students" ? queryResults : undefined}
                  parents={peopleA === "parents" ? queryResults : undefined}
                  tutors={peopleA === "tutors" ? queryResults : undefined}
                  org_id={orgId}
                />
              </Dialog>
            </div>
          </div>
        )}
      </pre>
    </div>
  );
}
